import { useEffect, useRef, useState } from 'react';
import type { TaskStatusResponse } from '../types';
import { HIDDEN_POLL_AFTER_MS, POLL_AFTER_MS } from '../constants';
import { evaluationService } from '../services/evaluation';

const TERMINAL = new Set(['success', 'partial_failed', 'failed', 'timeout']);
export function useTaskPolling(taskId?: string) {
  const [task, setTask] = useState<TaskStatusResponse | null>(null);
  const [error, setError] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const startedAt = useRef(0);
  useEffect(() => {
    if (!taskId) {
      setTask(null);
      setElapsed(0);
      return;
    }
    startedAt.current = Date.now();
    let timer = 0;
    let active = true;
    let controller: AbortController | null = null;
    const poll = async () => {
      controller?.abort();
      controller = new AbortController();
      try {
        const result = await evaluationService.status(taskId, controller.signal);
        if (!active) return;
        setTask(result);
        setError('');
        setElapsed(Math.floor((Date.now() - startedAt.current) / 1000));
        if (!TERMINAL.has(result.status))
          timer = window.setTimeout(
            poll,
            document.hidden ? HIDDEN_POLL_AFTER_MS : result.next_poll_after_ms || POLL_AFTER_MS
          );
      } catch (e) {
        if (!active || (e instanceof DOMException && e.name === 'AbortError')) return;
        setError(e instanceof Error ? e.message : '任务状态获取失败');
        timer = window.setTimeout(poll, HIDDEN_POLL_AFTER_MS);
      }
    };
    void poll();
    return () => {
      active = false;
      clearTimeout(timer);
      controller?.abort();
    };
  }, [taskId]);
  return { task, error, elapsed };
}
